import type { InvitationPreview } from "@fts/core";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { FormError, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { TurnstileWidget, useCaptcha } from "../components/Turnstile";
import { Note } from "../components/ui";
import { DataError } from "../data";
import { LANGS, useI18n, type MessageKey } from "../i18n";
import { errorCode } from "../lib/errors";
import { formatDate } from "../lib/format";
import { navigate } from "../router/hash";
import { useSession } from "../state/session";

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Signed-out or signed-in landing page for `#/invite/:token` (outside the console shell). */
export function InvitePage({ token }: { token: string }) {
  const { t, lang, setLang, locale } = useI18n();
  const { provider, status, user } = useSession();
  const [preview, setPreview] = useState<InvitationPreview | null | "loading" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    setPreview("loading");
    provider
      .previewInvitation(token)
      .then((p) => !cancelled && setPreview(p))
      .catch(() => !cancelled && setPreview("error"));
    return () => {
      cancelled = true;
    };
  }, [provider, token]);

  let content: ReactNode;
  if (preview === "loading" || status === "loading")
    content = (
      <div className="loading" role="status">
        <span className="spinner" aria-hidden="true" />
        {t("common.loading")}
      </div>
    );
  else if (preview === "error") content = <Problem icon="alert" title={t("common.loadError")} body={t("acceptInvite.loadErrorBody")} />;
  else if (preview === null) content = <Problem icon="alert" title={t("acceptInvite.unknownTitle")} body={t("acceptInvite.unknownBody")} />;
  else if (preview.status !== "pending") {
    const key: Record<Exclude<InvitationPreview["status"], "pending">, [MessageKey, MessageKey]> = {
      accepted: ["acceptInvite.acceptedTitle", "acceptInvite.acceptedBody"],
      revoked: ["acceptInvite.revokedTitle", "acceptInvite.revokedBody"],
      expired: ["acceptInvite.expiredTitle", "acceptInvite.expiredBody"],
    };
    const [title, body] = key[preview.status];
    content = (
      <>
        <InviteSummary preview={preview} />
        <Problem icon={preview.status === "accepted" ? "check" : "clock"} title={t(title)} body={t(body, { tenant: preview.tenantName })} />
        {status === "ready" && (
          <a href="#/" className="button button-secondary button-block">
            {t("acceptInvite.toConsole")}
          </a>
        )}
      </>
    );
  } else
    content = (
      <>
        <InviteSummary preview={preview} />
        <p className="invite-expiry">{t("acceptInvite.expires", { date: formatDate(preview.expiresAt, locale) ?? "" })}</p>
        {status === "ready" && user ? <AcceptForm token={token} /> : <SignInForInvite />}
      </>
    );

  return (
    <div className="login-page">
      <div className="login-lang lang-switch" role="group" aria-label={t("topbar.language")}>
        {LANGS.map((l) => (
          <button key={l} type="button" aria-pressed={lang === l} className={lang === l ? "is-on" : undefined} onClick={() => setLang(l)}>
            {l === "tr" ? t("lang.tr") : t("lang.en")}
          </button>
        ))}
      </div>
      <main className="login-card" aria-labelledby="invite-title">
        <img src="/brand/expartise-logo.png" alt={t("app.logoAlt")} className="login-logo" width={180} height={57} />
        <div className="login-product">{t("app.productLabel")}</div>
        <h1 id="invite-title" className="login-title">
          {t("acceptInvite.title")}
        </h1>
        {provider.mode === "demo" && <span className="demo-chip demo-chip-inline">{t("app.demoBadge")}</span>}
        <div className="invite-body">{content}</div>
      </main>
      <footer className="login-footer">{t("login.footer")}</footer>
    </div>
  );
}

function InviteSummary({ preview }: { preview: InvitationPreview }) {
  const { t } = useI18n();
  return (
    <p className="invite-summary">
      {t("acceptInvite.summary", { tenant: preview.tenantName, role: t(`role.${preview.role}`) })}
    </p>
  );
}

function Problem({ icon, title, body }: { icon: "alert" | "check" | "clock"; title: string; body: string }) {
  return (
    <div className="invite-problem" role={icon === "alert" ? "alert" : "status"}>
      <Icon name={icon} size={20} />
      <div>
        <p className="strong">{title}</p>
        <p className="muted">{body}</p>
      </div>
    </div>
  );
}

function SignInForInvite() {
  const { t } = useI18n();
  const { provider, signIn } = useSession();
  const [mode, setMode] = useState<"link" | "password">(provider.mode === "demo" ? "password" : "link");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  // Supabase Auth CAPTCHA applies to these sign-ins too (ADR 0020).
  const captcha = useCaptcha();

  const emailError = touched ? (!email.trim() ? t("form.required") : !EMAIL_PATTERN.test(email.trim()) ? t("error.invalid_email") : null) : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!EMAIL_PATTERN.test(email.trim()) || (mode === "password" && !password)) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === "link") {
        await provider.sendSignInLink(email.trim(), window.location.href, { captchaToken: captcha.token ?? undefined });
        setSentTo(email.trim());
        setBusy(false);
      } else {
        await signIn(email.trim(), password, { captchaToken: captcha.token ?? undefined });
        // The session switches to "ready"; this page then shows the accept step.
      }
    } catch (err) {
      setError(err);
      setBusy(false);
    }
    if (captcha.enabled) captcha.reset();
  };

  if (sentTo) {
    return (
      <div className="invite-sent" role="status">
        <Icon name="mail" size={20} />
        <div>
          <p className="strong">{t("acceptInvite.checkInbox")}</p>
          <p className="muted">{t("acceptInvite.checkInboxBody", { email: sentTo })}</p>
          <button type="button" className="link-button" onClick={() => setSentTo(null)}>
            {t("acceptInvite.useOtherEmail")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="login-form">
      <p className="muted small">{mode === "link" ? t("acceptInvite.signInLinkIntro") : t("acceptInvite.signInPasswordIntro")}</p>
      <FormField id="invite-signin-email" label={t("login.email")} error={emailError}>
        <input {...fieldAria("invite-signin-email", { error: emailError })} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
      </FormField>
      {mode === "password" && (
        <FormField id="invite-signin-password" label={t("login.password")}>
          <input id="invite-signin-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </FormField>
      )}
      <TurnstileWidget captcha={captcha} action="invite" />
      {error !== null && <FormError error={error} />}
      <button type="submit" className="button button-primary button-block" disabled={busy || !captcha.ready}>
        {busy ? t("common.working") : mode === "link" ? t("login.sendLink") : t("login.submit")}
      </button>
      {provider.mode === "demo" ? (
        <Note>{t("acceptInvite.demoNote")}</Note>
      ) : (
        <button type="button" className="link-button" onClick={() => setMode(mode === "link" ? "password" : "link")}>
          {mode === "link" ? t("acceptInvite.usePassword") : t("acceptInvite.useLink")}
        </button>
      )}
    </form>
  );
}

function AcceptForm({ token }: { token: string }) {
  const { t } = useI18n();
  const { provider, user, signOut, refreshUser } = useSession();
  const hasName = !!user && user.displayName.trim() !== "" && user.displayName !== user.email;
  const [name, setName] = useState(hasName ? user!.displayName : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const code = error !== null ? errorCode(error) : null;

  const accept = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const tenantId = await provider.acceptInvitation(token);
      if (name.trim() && name.trim() !== user?.displayName) {
        // Membership exists now; a failed name update must not hide that.
        await provider.updateDisplayName(name.trim()).catch(() => undefined);
      }
      await refreshUser(tenantId);
      navigate("/", undefined, { replace: true });
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={accept} noValidate className="login-form">
      <p className="muted small">{t("acceptInvite.signedInAs", { email: user?.email ?? "" })}</p>
      <FormField id="invite-name" label={t("acceptInvite.displayName")} optional hint={t("acceptInvite.displayNameHint")}>
        <input {...fieldAria("invite-name", { hint: true })} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      </FormField>
      {code === "wrong_email" ? (
        <p className="form-error" role="alert">
          {t("acceptInvite.wrongEmail", { email: user?.email ?? "" })}
        </p>
      ) : code === "already_member" ? (
        <p className="form-error" role="alert">
          {t("acceptInvite.alreadyMember")}
        </p>
      ) : (
        error !== null && <FormError error={error instanceof DataError ? error : new DataError("unknown")} />
      )}
      {code === "already_member" ? (
        <a href="#/" className="button button-primary button-block">
          {t("acceptInvite.toConsole")}
        </a>
      ) : (
        <button type="submit" className="button button-primary button-block" disabled={busy}>
          {busy ? t("common.working") : t("acceptInvite.accept")}
        </button>
      )}
      {code === "wrong_email" && (
        <button type="button" className="button button-secondary button-block" onClick={() => void signOut()}>
          {t("acceptInvite.switchAccount")}
        </button>
      )}
    </form>
  );
}
